import { initializeApp } from "firebase/app";
import { deleteToken, getMessaging, getToken, isSupported, onMessage, type Messaging } from "firebase/messaging";
import { identity } from "./api";

/**
 * Browser push through Firebase Cloud Messaging (backend contract: notifications.md, "Push").
 *
 * 1. `enablePush` asks the browser for permission (from a click), gets this browser's FCM token,
 *    and sends it to Identity's `me/push-tokens` as the admin app. The BFF adds the apps'
 *    `X-App-Key`.
 * 2. A push arriving while a tab is in front reaches `onForegroundPush`; otherwise
 *    `public/firebase-messaging-sw.js` shows it as a system notification.
 * 3. `disablePush` (sign-out) deletes the token at Firebase; Identity drops it on its next send.
 *
 * The Firebase web config below is public by design (it ships in every Firebase web app); the
 * service-account key that can *send* lives only on the backend. Empty config = push off.
 */
export type PushState = "unsupported" | "default" | "granted" | "denied";

const config = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY ?? "",
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN ?? "",
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID ?? "",
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID ?? "",
  appId: import.meta.env.VITE_FIREBASE_APP_ID ?? "",
};
const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY ?? "";

let messaging: Promise<Messaging | null> | undefined;

/** Firebase Messaging, or null when unconfigured or the browser cannot do push (e.g. Safari outside a home-screen app). */
function fcm() {
  messaging ??= (async () => {
    if (!config.apiKey || !vapidKey || !(await isSupported())) return null;
    return getMessaging(initializeApp(config));
  })();
  return messaging;
}

export async function pushState(): Promise<PushState> {
  return (await fcm()) ? Notification.permission : "unsupported";
}

/**
 * Registers this browser for push. `prompt: false` (every page load) only re-sends the token when
 * permission was already granted — that is also how a refreshed token reaches the backend.
 */
export async function enablePush(prompt: boolean): Promise<PushState> {
  const messaging = await fcm();
  if (!messaging) return "unsupported";

  const permission = prompt ? await Notification.requestPermission() : Notification.permission;
  if (permission !== "granted") return permission;

  // The worker gets the same public config through its URL: a static file cannot read env.
  const serviceWorkerRegistration = await navigator.serviceWorker.register(
    `/firebase-messaging-sw.js?${new URLSearchParams(config)}`,
  );
  const token = await getToken(messaging, { vapidKey, serviceWorkerRegistration });
  await identity().POST("/api/identity/me/push-tokens", { body: { token, app: "admin" } });
  return "granted";
}

/** Calls `show` for each push that arrives while this tab is in front. Returns the unsubscribe. */
export async function onForegroundPush(
  show: (push: { title: string; body: string; link?: string }) => void,
): Promise<() => void> {
  const messaging = await fcm();
  if (!messaging) return () => {};
  return onMessage(messaging, ({ data }) =>
    show({ title: data?.title ?? "", body: data?.body ?? "", link: data?.link }),
  );
}

/** Sign-out: this browser stops receiving the account's pushes. Best effort. */
export async function disablePush(): Promise<void> {
  try {
    const messaging = await fcm();
    if (messaging && Notification.permission === "granted") await deleteToken(messaging);
  } catch {
    // Nothing registered, or Firebase unreachable: Identity drops the token when FCM rejects it.
  }
}
